import { useParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import Navbar from "../components/Navbar";
import posts from "../posts.json";
import Footer from "../components/Footer";

interface BlogPostMeta {
  title: string;
  date: string;
  description: string;
  author: string;
  slug: string;
  readTime: string;
  image: string;
}

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const [content, setContent] = useState<string | null>(null);
  const [meta, setMeta] = useState<BlogPostMeta | null>(null);

  useEffect(() => {
    if (!slug) return;

    const postMeta = posts.find((p) => p.slug === slug);
    if (!postMeta) {
      console.error("Post metadata not found");
      return;
    }

    setMeta(postMeta as BlogPostMeta);

    const fileUrl = `/posts/${slug}.md`;

    fetch(fileUrl)
      .then((res) => {
        if (!res.ok) throw new Error("Post content not found");
        return res.text();
      })
      .then((text) => {
        const stripped = text.replace(/^---[\s\S]*?---/, "").trim();
        setContent(stripped);
      })
      .catch((err) => {
        console.error(err);
        setContent(null);
      });
  }, [slug]);

  if (!meta || content === null) {
    return <p className="text-center mt-10">Loading...</p>;
  }

  return (
    <>
      <article className="max-w-4xl mx-auto px-6 py-16 mt-20">
        <Link 
          to="/blog" 
          className="inline-flex items-center text-gray-400 hover:text-white transition-colors mb-8"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Blog
        </Link>
        <header className="mb-12 text-center">
          <img
            src={meta.image}
            alt={meta.title}
            className="w-full h-80 object-cover rounded-lg shadow-lg mb-8"
          />
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4 leading-tight">{meta.title}</h1>
          <div className="flex justify-center items-center space-x-4 text-gray-400 text-sm">
            <span>By {meta.author}</span>
            <span>|</span>
            <span>{meta.date}</span>
            <span>|</span>
            <span>{meta.readTime}</span>
          </div>
        </header>

        <div className="prose prose-invert max-w-none text-lg leading-relaxed">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
          >
            {content}
          </ReactMarkdown>
        </div>
      </article>
    </>
  );
}